import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Scale, FileText, Heart, FolderOpen, Users, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { services, siteConfig } from '@/data/mock';

const Services = () => {
  const getIcon = (iconName) => {
    const icons = {
      MessageCircle: MessageCircle,
      Scale: Scale,
      FileText: FileText,
      Heart: Heart,
      FolderOpen: FolderOpen,
      Users: Users,
    };
    const Icon = icons[iconName] || MessageCircle;
    return <Icon className="w-7 h-7" />;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)] pattern-bg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
              Kostenlose Unterstützung
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Meine Leistungen
            </h1>
            <p className="text-xl text-[hsl(215,15%,45%)] leading-relaxed">
              Praktische Hilfe und Orientierung für Väter in schwierigen Familiensituationen – 
              kostenlos und vertraulich.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.id} 
                className="card-hover border-[hsl(210,25%,90%)] bg-white group"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] flex items-center justify-center mb-6 group-hover:bg-[hsl(213,80%,35%)] group-hover:text-white transition-all duration-300">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-[hsl(215,15%,45%)] leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-4">
              So läuft es ab
            </h2>
            <p className="text-lg text-[hsl(215,15%,45%)] max-w-2xl mx-auto">
              Einfach, unkompliziert und völlig unverbindlich
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Kontakt aufnehmen',
                description: 'Schreiben Sie mir oder buchen Sie direkt einen Termin online.'
              },
              {
                step: '2',
                title: 'Erstgespräch',
                description: 'In 30-45 Minuten besprechen wir Ihre Situation in Ruhe.'
              },
              {
                step: '3',
                title: 'Orientierung',
                description: 'Ich gebe Ihnen praktische Tipps und zeige mögliche Wege auf.'
              },
              {
                step: '4',
                title: 'Vermittlung',
                description: 'Bei Bedarf vermittle ich Sie an passende Fachleute weiter.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(213,80%,35%)] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-[hsl(215,25%,15%)] mb-3">
                  {item.title}
                </h3>
                <p className="text-[hsl(215,15%,45%)]">
                  {item.description}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute right-0 top-8 transform translate-x-1/2">
                    <ArrowRight className="w-6 h-6 text-[hsl(210,25%,80%)]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)] mb-6">
                Warum Väter zu mir kommen
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Echte Erfahrung',
                    description: 'Ich kenne das System aus eigener Erfahrung und weiß, worauf es ankommt.'
                  },
                  {
                    title: 'Keine Kosten',
                    description: 'Meine Beratung ist und bleibt kostenlos. Keine versteckten Gebühren.'
                  },
                  {
                    title: 'Schnelle Termine',
                    description: 'In der Regel kann ich innerhalb von 2-3 Tagen einen Termin anbieten.'
                  },
                  {
                    title: 'Absolute Vertraulichkeit',
                    description: 'Alles, was Sie mir erzählen, bleibt unter uns. Versprochen.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-[hsl(160,60%,40%)] mr-4 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[hsl(215,25%,15%)] mb-1">{benefit.title}</h4>
                      <p className="text-[hsl(215,15%,45%)]">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Card */}
            <Card className="border-[hsl(210,25%,90%)] bg-[hsl(210,40%,98%)]">
              <CardContent className="p-8">
                <div className="flex items-center mb-6">
                  <Clock className="w-8 h-8 text-[hsl(213,80%,35%)] mr-4" />
                  <div>
                    <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)]">Erstgespräch</h3>
                    <p className="text-[hsl(215,15%,45%)]">30-45 Minuten</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    '100% kostenlos und unverbindlich',
                    'Per Telefon oder Video-Call',
                    'Termine auch abends möglich',
                    'Keine Vorbereitung nötig'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center text-[hsl(215,25%,30%)]">
                      <CheckCircle className="w-5 h-5 text-[hsl(160,60%,40%)] mr-3" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
                  <Link to="/kontakt">
                    Jetzt Termin buchen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[hsl(213,80%,35%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Der erste Schritt zählt
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Sie müssen nicht alles alleine schaffen. Manchmal braucht es nur jemanden, 
            der zuhört und den Weg kennt.
          </p>
          <Button asChild size="lg" className="bg-white text-[hsl(213,80%,35%)] hover:bg-white/90 shadow-lg text-base px-8">
            <Link to="/kontakt">
              Kostenloses Gespräch vereinbaren
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
